// 初始化 WebGL 上下文
function initWebGL(canvas: HTMLCanvasElement): WebGLRenderingContext | null {
  const gl = canvas.getContext('webgl', {
    alpha: false, // 禁用透明度缓冲
    depth: true, // 启用深度缓冲
    stencil: false, // 禁用模板缓冲
    antialias: true, // 启用抗锯齿
    premultipliedAlpha: false, // 禁用预乘透明度
    preserveDrawingBuffer: false // 禁用绘图缓冲区保留
  })

  if (!gl) {
    console.error('WebGL not supported')
    return null
  }

  return gl
}

// 创建着色器
function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) return null

  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compilation error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }
  return shader
}

// 创建着色器程序
function createProgram(
  gl: WebGLRenderingContext,
  vertexShaderSource: string,
  fragmentShaderSource: string
): WebGLProgram | null {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)
  if (!vertexShader || !fragmentShader) return null

  const program = gl.createProgram()
  if (!program) return null

  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program linking error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }
  return program
}

// 初始化缓冲区
function initBuffer(gl: WebGLRenderingContext): WebGLBuffer | null {
  const vertices = new Float32Array([-0.5, -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, 0.5])

  const buffer = gl.createBuffer()
  if (!buffer) return null

  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)
  return buffer
}

// 配置纹理
function initTexture(gl: WebGLRenderingContext): WebGLTexture | null {
  const texture = gl.createTexture()
  if (!texture) return null

  gl.bindTexture(gl.TEXTURE_2D, texture)

  // 设置纹理参数
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

  // 提供默认纹理数据
  const pixel = new Uint8Array([0, 0, 255, 255]) // 蓝色
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixel)

  return texture
}

// 渲染函数
function render(
  gl: WebGLRenderingContext,
  program: WebGLProgram,
  buffer: WebGLBuffer,
  texture: WebGLTexture
): void {
  gl.clearColor(0.0, 0.0, 0.0, 1.0)
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)

  gl.useProgram(program)

  // 绑定缓冲区并设置顶点属性
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  const positionLocation = gl.getAttribLocation(program, 'aPosition')
  gl.enableVertexAttribArray(positionLocation)
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

  // 设置纹理
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  const texLocation = gl.getUniformLocation(program, 'uTexture')
  gl.uniform1i(texLocation, 0)

  // 绘制
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
}

// 获取 WebGL 参数信息
function getUnmaskedInfo(gl: WebGLRenderingContext) {
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
  if (debugInfo) {
    return {
      vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
      renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    }
  }
  return {
    vendor: gl.getParameter(gl.VENDOR),
    renderer: gl.getParameter(gl.RENDERER)
  }
}

function getWebGLParameters(gl: WebGLRenderingContext): Record<string, any> {
  const unmaskedInfo = getUnmaskedInfo(gl)

  // 处理渲染器信息
  const rendererInfo = {
    raw: unmaskedInfo.renderer,
    gpu:
      unmaskedInfo.renderer.match(
        /(?:ANGLE.[^,]*,\s*)?(AMD|NVIDIA|Intel|Apple)[\s\w]+(?=\s*(?:OpenGL|Metal|Direct3D|$))/
      )?.[0] || unmaskedInfo.renderer,
    backend: (
      unmaskedInfo.renderer.match(/(?:OpenGL|Metal|Direct3D|ANGLE)/)?.[0] || 'Unknown'
    ).toLowerCase()
  }

  // 处理供应商信息
  const vendorInfo = {
    raw: unmaskedInfo.vendor,
    name: unmaskedInfo.vendor
      .replace(/Google Inc. \((.*?)\)/, '$1')
      .replace(/(Apple Inc\.|Intel Inc\.)/, '$1')
      .trim()
  }

  // 获取更多 WebGL 能力参数
  const capabilities = {
    // 基础参数
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
    maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
    maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
    maxVertexTextureImageUnits: gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),

    // 颜色缓冲区参数
    redBits: gl.getParameter(gl.RED_BITS),
    greenBits: gl.getParameter(gl.GREEN_BITS),
    blueBits: gl.getParameter(gl.BLUE_BITS),
    alphaBits: gl.getParameter(gl.ALPHA_BITS),

    // 深度和模板缓冲区参数
    depthBits: gl.getParameter(gl.DEPTH_BITS),
    stencilBits: gl.getParameter(gl.STENCIL_BITS),

    // 最大渲染缓冲区大小
    maxRenderBufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),

    // 支持的压缩纹理格式
    compressedTextureFormats: gl.getParameter(gl.COMPRESSED_TEXTURE_FORMATS)
  }

  // 获取扩展功能支持情况
  const extensionsMap = (gl.getSupportedExtensions() || []).reduce(
    (acc, ext) => {
      acc[ext] = true
      return acc
    },
    {} as Record<string, boolean>
  )

  // 获取上下文属性
  const contextAttributes = gl.getContextAttributes() || {}

  return {
    // 基本信息（可能被限制）
    renderer: rendererInfo,
    vendor: vendorInfo,

    // 详细的硬件能力指标（这些在 Safari 中也是可用的）
    capabilities,

    // 扩展支持情况（作为对象而不是数组，方便比较）
    extensions: extensionsMap,

    // 上下文配置
    contextConfig: {
      alpha: contextAttributes.alpha,
      depth: contextAttributes.depth,
      stencil: contextAttributes.stencil,
      antialias: contextAttributes.antialias,
      premultipliedAlpha: contextAttributes.premultipliedAlpha,
      preserveDrawingBuffer: contextAttributes.preserveDrawingBuffer,
      powerPreference: contextAttributes.powerPreference
    },

    // 浏览器环境信息
    platform: {
      userAgent: navigator.userAgent,
      isElectron: navigator.userAgent.includes('Electron'),
      isMobile: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
      // 添加浏览器类型检测
      browser: {
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        isChrome: /chrome|chromium/i.test(navigator.userAgent),
        isFirefox: /firefox/i.test(navigator.userAgent),
        isEdge: /edg/i.test(navigator.userAgent)
      },
      // 添加操作系统检测
      os: {
        isMac: /Mac/i.test(navigator.platform),
        isWindows: /Win/i.test(navigator.platform),
        isLinux: /Linux/i.test(navigator.platform),
        isIOS: /iPhone|iPad|iPod/i.test(navigator.platform)
      }
    }
  }
}

// 获取 WebGL 渲染结果的像素数据
function getWebGLRenderingHash(
  gl: WebGLRenderingContext,
  program: WebGLProgram,
  buffer: WebGLBuffer,
  texture: WebGLTexture
): string {
  render(gl, program, buffer, texture)

  const width = gl.canvas.width
  const height = gl.canvas.height
  const pixels = new Uint8Array(width * height * 4)
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels)

  return Array.from(pixels)
    .reduce((hash, val) => (hash << 5) - hash + val, 0)
    .toString(36)
}

// 生成 WebGL 指纹
function generateWebGLInfor(): Record<string, any> | null {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 256 // 设置合适的画布大小
    canvas.height = 256

    const gl = initWebGL(canvas)
    if (!gl) return null

    const parameters = getWebGLParameters(gl)

    // 更新着色器代码以支持纹理
    const vertexShaderSource = `
      attribute vec2 aPosition;
      varying vec2 vTexCoord;
      void main() {
        vTexCoord = aPosition * 0.5 + 0.5;
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `

    const fragmentShaderSource = `
      precision mediump float;
      varying vec2 vTexCoord;
      uniform sampler2D uTexture;
      void main() {
        vec4 texColor = texture2D(uTexture, vTexCoord);
        gl_FragColor = texColor;
      }
    `

    const program = createProgram(gl, vertexShaderSource, fragmentShaderSource)
    if (!program) return null

    const buffer = initBuffer(gl)
    if (!buffer) return null

    const texture = initTexture(gl)
    if (!texture) return null

    // 获取包含纹理渲染的哈希值
    const renderHash = getWebGLRenderingHash(gl, program, buffer, texture)
    console.info('renderHash', renderHash)

    const fingerprint = {
      ...parameters,
      renderHash
    }

    return fingerprint
  } catch (error) {
    console.error('Error generating WebGL fingerprint:', error)
    return null
  }
}

// 如果需要立即执行测试，可以保留 main() 函数
// main();

// 导出主要函数供其他模块使用
// export {
//   initWebGL,
//   createShader,
//   createProgram,
//   initBuffer,
//   initTexture,
//   render,
// };

export function generateWebGLFingerprint() {
  const fingerprint = generateWebGLInfor()
  if (fingerprint) {
    const data = fingerprint

    return {
      maxTextureSize: data.capabilities.maxTextureSize,
      colorBits: {
        red: data.capabilities.redBits,
        green: data.capabilities.greenBits,
        blue: data.capabilities.blueBits,
        alpha: data.capabilities.alphaBits
      },
      depthBits: data.capabilities.depthBits,
      maxRenderBufferSize: data.capabilities.maxRenderBufferSize,
      maxViewportDims: data.capabilities.maxViewportDims,
      webgl2: data.extensions['WEBGL_debug_renderer_info'],
      angleInstanced: data.extensions['ANGLE_instanced_arrays'],
      textureFloat: data.extensions['OES_texture_float'],
      renderHash: data.renderHash
    }
  }
  return null
}
